import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import axios from '../../../libraries/axios/axios';
import { apiRoutes } from '../../../config/api/apiRoutes';
import { useSelectedVehicleQuery } from '../../../api/vehicle/queries';
import {
  savedVehicleIdsState,
  selectedVehicleIdState,
} from '../../../atoms/vehicle/vehicle.atom';
import { translationsState } from '../../../atoms/translation/translation.atom';
import { translate } from '../../../utils/translation/translation';
import {
  clearLocalStorageValue,
  getLocalStorageValue,
  LocalStorageKey,
  setLocalStorageValue,
} from '../../../utils/localStorage/localStorage';
import { isPrerender } from '../../../utils/seo/prerender.util';
import { SelectedVehicle } from '../../../domain/Vehicle';

// The API's answer for a van, bus or chassis cab: the shop does not sell
// their parts and no longer lists them (backend vehicle-visibility.ts).
export const VEHICLE_NOT_SUPPORTED = 'VEHICLE.NOT_SUPPORTED';
export const VEHICLE_NOT_SUPPORTED_FALLBACK =
  'We only sell parts for cars, not for vans, buses or chassis cabs.';

export const isVehicleNotSupportedError = (error: unknown): boolean =>
  (error as { response?: { data?: { message?: unknown } } } | null)?.response
    ?.data?.message === VEHICLE_NOT_SUPPORTED;

const storedSavedVehicleIds = (): number[] =>
  (getLocalStorageValue<number[]>(LocalStorageKey.savedVehicles) ?? [])
    .map(Number)
    .filter((id) => id > 0);

/**
 * Drops vans (and buses, chassis cabs) a visitor chose before the shop
 * stopped listing them. Mounted once.
 *
 * - The selected one: its id used to stay in storage for good - the listing
 *   went unfiltered and the "fits your car" line spun forever. It is cleared
 *   and the visitor is told why. Every other user of the selected vehicle
 *   shares this query, so nothing else changes.
 * - Saved ones: the garage list no longer shows them, but the garage badge
 *   counts the stored ids. Once per visit the stored ids are checked against
 *   what the API still serves and the rest are dropped.
 */
const useDropUnsupportedVehicle = () => {
  const [selectedVehicleId, setSelectedVehicleId] = useRecoilState(
    selectedVehicleIdState,
  );
  const setSavedVehicleIds = useSetRecoilState(savedVehicleIdsState);
  const translations = useRecoilValue(translationsState);
  const { error } = useSelectedVehicleQuery(selectedVehicleId);
  const savedChecked = useRef(false);

  useEffect(() => {
    if (
      isPrerender() ||
      !selectedVehicleId ||
      !isVehicleNotSupportedError(error)
    ) {
      return;
    }

    clearLocalStorageValue(LocalStorageKey.selectedVehicle);
    setSelectedVehicleId(null);

    const saved = storedSavedVehicleIds().filter(
      (id) => id !== Number(selectedVehicleId),
    );
    setLocalStorageValue(LocalStorageKey.savedVehicles, saved);
    setSavedVehicleIds(saved);

    const text = translate(translations, VEHICLE_NOT_SUPPORTED);
    toast.error(
      text === VEHICLE_NOT_SUPPORTED ? VEHICLE_NOT_SUPPORTED_FALLBACK : text,
    );
  }, [error, selectedVehicleId]);

  useEffect(() => {
    if (savedChecked.current || isPrerender()) {
      return;
    }

    const saved = storedSavedVehicleIds();

    if (saved.length === 0) {
      return;
    }

    savedChecked.current = true;

    axios
      .get<SelectedVehicle[]>(apiRoutes.vehicles.getVehiclesByIds, {
        params: { ids: saved },
      })
      .then(({ data }) => {
        // Only a real answer may drop anything.
        if (!Array.isArray(data)) {
          return;
        }

        const served = new Set(data.map((vehicle) => Number(vehicle.id)));
        const current = storedSavedVehicleIds();
        const kept = current.filter(
          (id) => served.has(id) || !saved.includes(id),
        );

        if (kept.length === current.length) {
          return;
        }

        setLocalStorageValue(LocalStorageKey.savedVehicles, kept);
        setSavedVehicleIds(kept);
      })
      .catch(() => undefined);
  }, []);
};

export default useDropUnsupportedVehicle;
